import type { ReactNode } from "react";
import { Link } from "wouter";
import { Building2, ShieldAlert } from "lucide-react";
import { PageHead } from "@/components/bits";
import { useDashboard } from "@/features/dashboard/dashboard-context";
import { useAuth } from "@/features/auth/auth-context";
import { SkeletonRows } from "@/features/finance/kit";

/**
 * Business modules require an ACTIVE, KYB-approved BUSINESS account.
 * Children are not mounted (and therefore make no API request) until it passes.
 */
export function BusinessGate({
  title,
  what,
  children,
}: {
  title: string;
  what: string;
  children: ReactNode;
}) {
  const d = useDashboard();
  const { identity } = useAuth();
  const ext = d as unknown as { financialAllowed?: boolean };
  const isBusiness = d.isBusiness || identity?.accountType === "BUSINESS";
  const status = identity?.accountStatus?.toUpperCase();

  if (d.loading)
    return (
      <>
        <PageHead title={title} />
        <SkeletonRows n={4} />
      </>
    );

  if (!isBusiness)
    return (
      <>
        <PageHead title={title} />
        <Gate
          testid="gate-business-required"
          icon={<Building2 className="h-5 w-5" />}
          title={`${what} est réservé aux comptes entreprise`}
          body="Créez un espace entreprise depuis vos comptes. Votre compte particulier reste intact ; l'espace entreprise s'active après approbation du KYB."
          href="/accounts"
          cta="Passer à un compte entreprise"
        />
      </>
    );

  if (
    d.locked ||
    ext.financialAllowed === false ||
    (status && status !== "ACTIVE")
  )
    return (
      <>
        <PageHead title={title} />
        <Gate
          testid="gate-kyb-required"
          icon={<ShieldAlert className="h-5 w-5" />}
          title={`${what} indisponible : KYB requis`}
          body="Ce module s'ouvre lorsque l'entreprise est active et que son dossier KYB est approuvé. Aucune donnée n'est chargée avant cette validation."
          href="/verification"
          cta="Compléter le KYB"
        />
      </>
    );

  return <>{children}</>;
}

function Gate({
  icon,
  title,
  body,
  href,
  cta,
  testid,
}: {
  icon: ReactNode;
  title: string;
  body: string;
  href: string;
  cta: string;
  testid: string;
}) {
  return (
    <div
      data-testid={testid}
      className="rise flex flex-col items-start gap-4 rounded-xl border border-dashed border-primary/30 bg-accent/50 p-6 sm:p-8"
    >
      <span className="flex h-11 w-11 items-center justify-center rounded-full bg-primary/10 text-primary">
        {icon}
      </span>
      <div>
        <p className="text-base font-bold">{title}</p>
        <p className="mt-1 max-w-lg text-sm leading-relaxed text-muted-foreground">
          {body}
        </p>
      </div>
      <Link
        href={href}
        className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3.5 py-2 text-sm font-semibold text-primary-foreground transition hover:brightness-110"
      >
        {cta}
      </Link>
    </div>
  );
}
